import { Box, ClickAwayListener, Popper } from '@mui/material';
import { type ReactNode } from 'react';
import { useRecoilState } from 'recoil';
import {
    type FloatingPanelId,
    openFloatingPanelState,
} from '~/state/atoms/toolsAtoms';

interface FloatingPanelProps {
    panelId: FloatingPanelId;
    anchorEl: HTMLElement | null;
    placement?: 'right-start' | 'left-start' | 'bottom';
    children: ReactNode;
}

export default function FloatingPanel({
    panelId,
    anchorEl,
    placement = 'right-start',
    children,
}: FloatingPanelProps) {
    const [openPanel, setOpenPanel] = useRecoilState(openFloatingPanelState);
    const isOpen = openPanel === panelId && anchorEl !== null;

    const handleClickAway = (event: MouseEvent | TouchEvent) => {
        // Don't close if clicking within the toolbar itself
        const target = event.target as HTMLElement;
        if (target.closest('[data-toolbar-id]')) {
            return;
        }
        setOpenPanel(null);
    };

    if (!isOpen) {
        return null;
    }

    return (
        <Popper
            open
            anchorEl={anchorEl}
            placement={placement}
            modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
            sx={{ zIndex: 1100 }}
        >
            <ClickAwayListener onClickAway={handleClickAway}>
                <Box
                    sx={(theme) => ({
                        p: 1.5,
                        width: 280,
                        borderRadius: `${theme.shape.borderRadius}px`,
                        maxHeight: 'calc(100vh - 120px)',
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        ...theme.mixins.glassOverlay,
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.27)',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                    })}
                >
                    {children}
                </Box>
            </ClickAwayListener>
        </Popper>
    );
}
